import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer
} from "react";
import { useCreateAccessTokenMutation } from "generated/graphql";
import { useTranslation } from "utilities/i18n";
import { generatePath, infiniteNestLink, Link, Switch } from "polyrouter";
import { Layout } from "views/Main/Layouts";
import {
  getColumns,
  HEADER_ID,
  itemCheckedWithSet,
  sortTableData,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableHeaderItem,
  TableSearchEdit,
  toggleAll,
  toggleItemWithSet
} from "collections/Table/Table";
import { DISMISS, useNotification } from "collections/Notifications";
import {
  CopyToClipboardButton,
  Loader,
  NoResultsIndicatorAnimation,
  PolyIconButton
} from "elements";
import { DeleteAccessTokensModal } from "../Delete/DeleteAccessTokensModal";
import { SortState } from "store/filters";
import { ProtectedButton, ProtectedLink, ProtectedRoute } from "TsRoleGuards";
import { AccessToken, useGetAccessTokenList } from "../useAccessTokens";
import { toLicenseDateLocaleDateString } from "utilities/licenseServerUtils";
import "./AccessTokenList.scss";
import {
  AccessTokenListReducer,
  defaultAccessTokenListState,
  updateDataLoaded,
  updatePageInfo,
  updateSearch,
  updateSelectedItems,
  updateSort,
  updateVisibleAccessTokens
} from "./AccessTokenListReducer";
import { TABLE_COLUMN_STATE, TABLES } from "store/userMeta";
import { useSelector } from "react-redux";
import { State } from "store";
import { generateTableHeaders } from "utilities/tableUtils";
import { toPartialHiddenText } from "utilities/stringUtils";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";

type AccessTokenRowRowProps = {
  accessToken: AccessToken;
  accessTokenVisible: boolean;
  onSelectAccessTokenVisible: (id: string) => void;
  onCheck: (id: string) => void;
  checked: boolean;
  showChecks: boolean;
  shownColumns: Array<string>;
};

export const AccessTokenRow: React.FC<AccessTokenRowRowProps> = ({
  accessToken,
  accessTokenVisible,
  onSelectAccessTokenVisible,
  onCheck,
  checked,
  showChecks,
  shownColumns
}) => {
  const { t } = useTranslation();
  const { recordId, token, creationTime, lastUsedTime } = accessToken;

  const { COLUMNS } = TABLES.CLARITI_ACCESS_TOKENS;

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.TOKEN:
        content = (
          <div className="access-token-container">
            <CopyToClipboardButton
              data-testid="copy-access-token"
              name="copyToken"
              className="clickable mr-5"
              notifyName={t("ACCESS_TOKEN_NAME")}
              getTextToCopy={() => token}
              iconButton={true}
            />
            <PolyIconButton
              className="mr-5"
              icon={accessTokenVisible ? "eye_slash" : "eye"}
              onClick={() => onSelectAccessTokenVisible(recordId)}
              data-testid="toggle-access-token-visible"
            />
            <Link
              to={generatePath(
                "/manage/licensing/clariti/access-tokens/details/:recordId",
                { recordId }
              )}
              className="d-block"
            >
              {accessTokenVisible ? token : toPartialHiddenText(token)}
            </Link>
          </div>
        );
        break;
      case COLUMNS.CREATION_TIME:
        content = toLicenseDateLocaleDateString(creationTime);
        break;
      case COLUMNS.LAST_USED_TIME:
        content = toLicenseDateLocaleDateString(lastUsedTime);
        break;
    }

    return <td key={column}>{content}</td>;
  };

  return (
    <TableBulkItemRow
      checked={checked}
      onCheck={() => onCheck(recordId)}
      showChecks={showChecks}
    >
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  );
};

export const AccessTokenList: React.FC = () => {
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const { Content } = Layout;
  const { t } = useTranslation();
  const [
    {
      dataLoaded,
      selectedItems,
      sort,
      searchTerm,
      accessTokenCount,
      accessTokens,
      visibleAccessTokens
    },
    dispatch
  ] = useReducer(AccessTokenListReducer, defaultAccessTokenListState);

  const { NAME: TABLE_NAME, COLUMNS } = TABLES.CLARITI_ACCESS_TOKENS;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });
  const { data, fetching, stale } = useGetAccessTokenList({
    variables: {
      tenantId: tenantId
    }
  });

  const formattedSearchTerm = searchTerm?.toLowerCase() ?? undefined;

  // Since filtering is handled locally, we will automatically load all data so things work as expected
  const filteredData = !!formattedSearchTerm;

  // Tenant ID switch
  useEffect(() => {
    dispatch(updateDataLoaded(false));
    dispatch(updateSelectedItems([]));
  }, [tenantId]);

  const loading = fetching || stale;
  const accessTokensData = data;

  useEffect(() => {
    if (!loading) {
      dispatch(updateDataLoaded(true));

      if (accessTokensData) {
        dispatch(
          updatePageInfo({
            accessTokenCount: accessTokensData.totalCount,
            accessTokens: accessTokensData.edges.map(entry => {
              return { ...entry.node };
            })
          })
        );
      }
    }
  }, [loading, accessTokensData]);

  const setSelectedItems = useCallback((items: Array<string>) => {
    dispatch(updateSelectedItems(items));
  }, []);
  const setSort = useCallback((sort: SortState) => {
    dispatch(updateSort(sort));
  }, []);
  const setSearch = useCallback((search?: string) => {
    dispatch(updateSearch(search));
  }, []);
  const setVisibleAccessTokens = useCallback((items: Set<string>) => {
    dispatch(updateVisibleAccessTokens(items));
  }, []);

  const hasVisibleAccessTokens = visibleAccessTokens.size > 0;

  const toggleAccessTokenVisible = (id: string) => {
    const visible = !visibleAccessTokens.has(id);
    if (visible) {
      visibleAccessTokens.add(id);
    } else {
      visibleAccessTokens.delete(id);
    }
    setVisibleAccessTokens(visibleAccessTokens);
  };

  const toggleAllAccessTokensVisible = () => {
    const visible = !hasVisibleAccessTokens;
    let newVisibleAccessTokens: Set<string>;
    if (visible) {
      newVisibleAccessTokens = new Set(
        accessTokens.map(accessToken => accessToken.recordId)
      );
    } else {
      newVisibleAccessTokens = new Set();
    }
    setVisibleAccessTokens(newVisibleAccessTokens);
  };

  // Sort accessTokens
  sortTableData(accessTokens, sort);

  const toggleItem = toggleItemWithSet(
    accessTokens.length,
    selectedItems,
    setSelectedItems
  );
  const itemChecked = itemCheckedWithSet(selectedItems);

  // TODO: Aggregate and optimize selection state information
  const selectedAccessTokens = useMemo(
    () =>
      selectedItems.reduce((accessTokensList, recordId) => {
        const accessTokenObj = accessTokens.find(
          accessToken => accessToken.recordId === recordId
        );
        if (accessTokenObj) {
          accessTokensList.push(accessTokenObj);
        }
        return accessTokensList;
      }, [] as Array<AccessToken>),
    [selectedItems, accessTokens]
  );

  const columnTranslations = {
    [COLUMNS.TOKEN]: t("ACCESS_TOKEN_NAME"),
    [COLUMNS.CREATION_TIME]: t("DATE_CREATED"),
    [COLUMNS.LAST_USED_TIME]: t("LAST_USED")
  };

  const getTableHeaders = (column: string, title: string) => {
    let leftTitle;

    switch (column) {
      case COLUMNS.TOKEN:
        leftTitle = (
          <PolyIconButton
            className="mr-5"
            icon={hasVisibleAccessTokens ? "eye_slash" : "eye"}
            onClick={() => {
              toggleAllAccessTokensVisible();
            }}
            data-testid="toggle-all-access-tokens-visible"
          />
        );
        break;
    }

    return (
      <TableHeaderItem
        fieldName={column}
        leftTitle={leftTitle}
        title={title}
        sort={{
          currentSort: sort,
          onSort: setSort
        }}
        key={column}
      />
    );
  };

  const shown = getColumns(shownColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);

  const refresh = () => {
    dispatch(updateSelectedItems([]));
  };

  // Can be an expensive operation
  const filteredAccessTokens = useMemo(() => {
    return formattedSearchTerm
      ? accessTokens.filter(accessToken =>
          accessToken?.token?.toLowerCase().includes(formattedSearchTerm)
        )
      : accessTokens;
  }, [formattedSearchTerm, accessTokens]);

  const hasSelectedItems = selectedItems.length > 0;

  // Generate access token handler
  const [, createAccessToken] = useCreateAccessTokenMutation();
  const [addNotification] = useNotification();
  const generateAccessToken = useCallback(async () => {
    if (tenantId) {
      const userResponse = await createAccessToken({
        tenantId: tenantId
      });
      if (userResponse.data?.createAccessToken?.recordId) {
        refresh();
        addNotification({
          type: "success",
          dismiss: DISMISS.DEFAULT,
          message: t("SUCCESSFULLY_ADDED_TOKEN"),
          persistThroughNav: true
        });
      } else if (userResponse.error) {
        addNotification({
          type: "error",
          dismiss: DISMISS.DEFAULT,
          message: t("CREATE_TOKEN_ERROR", {
            message: userResponse.error.message
          })
        });
      }
    }
    // omit t and addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, createAccessToken]);

  const displayCount = filteredData
    ? filteredAccessTokens.length
    : accessTokenCount;

  return (
    <Content>
      <Loader loading={!dataLoaded}>
        {!loading && accessTokenCount === 0 ? (
          <NoResultsIndicatorAnimation title={t("NO_ACCESS_TOKENS_FOUND")}>
            <ProtectedButton
              className="btn btn-primary"
              onClick={generateAccessToken}
              requirement="license:WriteTenant"
            >
              {t("ADD_NEW")}
            </ProtectedButton>
          </NoResultsIndicatorAnimation>
        ) : (
          <>
            <div className="table-heading">
              <span className="metric" data-testid="access-token-count">
                {displayCount}
              </span>
              <span className="title ml-5">
                {t("ACCESS_TOKEN", { count: displayCount })}
              </span>
            </div>
            <div className="grouping mb-15 table-actions">
              <ProtectedButton
                className="btn btn-primary"
                onClick={generateAccessToken}
                requirement="license:WriteTenant"
              >
                {t("ADD_NEW")}
              </ProtectedButton>
              <ProtectedLink
                to={hasSelectedItems ? infiniteNestLink("/remove") : ""}
                className={hasSelectedItems ? "btn" : "btn disabled"}
                requirement="license:WriteTenant"
              >
                {t("REMOVE", {
                  count: selectedAccessTokens.length
                })}
              </ProtectedLink>
              <TableSearchEdit
                search={{
                  onSearch: setSearch,
                  searchPlaceholder: t("SEARCH_ACCESS_TOKENS")
                }}
                edit={{
                  tableName: TABLE_NAME,
                  shownColumns: shown,
                  hiddenColumns: hidden
                }}
              />
            </div>
            <div className="table-container">
              <table className="table custom-table">
                <TableBulkItemHeader
                  checked={itemChecked(HEADER_ID)}
                  onCheck={() =>
                    toggleAll(
                      accessTokens.map(accessToken => accessToken.recordId),
                      selectedItems,
                      setSelectedItems
                    )
                  }
                  showChecks={!!shownColumns.length}
                >
                  {generateTableHeaders(shown, getTableHeaders)}
                </TableBulkItemHeader>
                <TableBody
                  loading={loading}
                  cols={shown.length}
                  accommodateScrollbar
                >
                  {filteredAccessTokens.map(accessToken => {
                    return (
                      <AccessTokenRow
                        key={accessToken.recordId}
                        accessToken={accessToken}
                        accessTokenVisible={visibleAccessTokens.has(
                          accessToken.recordId
                        )}
                        onSelectAccessTokenVisible={toggleAccessTokenVisible}
                        onCheck={() => toggleItem(accessToken.recordId)}
                        checked={itemChecked(accessToken.recordId)}
                        showChecks={!!shownColumns.length}
                        shownColumns={shown.map(column => column.key)}
                      />
                    );
                  })}
                </TableBody>
              </table>
            </div>
            <Switch nest>
              <ProtectedRoute
                nest
                path="/remove"
                requirement="license:WriteTenant"
              >
                <DeleteAccessTokensModal
                  accessTokens={selectedAccessTokens}
                  refresh={refresh}
                />
              </ProtectedRoute>
            </Switch>
          </>
        )}
      </Loader>
    </Content>
  );
};
